import type { WorkspaceFeatures } from "@expert/features";

import { useFeatures } from "@expert/features";
import { useCallback, useEffect, useState } from "react";

import { getRootDispatcher } from "@/analytics/rootDispatcher";
import { env } from "@/config";
import { useAgentActivity, useAgentSdk, useAgentStore, useLogger, usePartner } from "@/sdk";
import { userCache } from "@/shared-utils";

import { emitExpertHubError, EXPERT_HUB_ACTIONS } from "../data/expertHubErrorMetric";
import { clearAutoAvailableDeadline, resolveCountdownDeadline } from "./autoAvailableStorage";
import { raceWithTimeout } from "./raceWithTimeout";
import { useAutoAvailableStore } from "./useAutoAvailableStore";

export const AUTO_AVAILABLE_MS = 15_000;
/** Upper bound on the status-change call so a stuck SDK can't hang the flow. */
const SET_AVAILABLE_TIMEOUT_MS = 3_000;

/**
 * Visual phase of the pill:
 * - `hidden`   — not shown.
 * - `counting` — countdown running.
 * - `waiting`  — moved to Available; "Waiting for a call".
 */
export type AutoAvailablePhase = "hidden" | "counting" | "waiting";

export function useAutoAvailableCountdown() {
    const logger = useLogger({ module: "AutoAvailableCountdown", supportTeam: "ui-orchestration" });
    const agentSdk = useAgentSdk();
    const partner = usePartner();
    const activity = useAgentStore((state) => state.activity);
    const { setNewAgentActivity } = useAgentActivity();
    const {
        features: { isExpertHubAutoAvailableEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const hasArmed = useAutoAvailableStore((state) => state.hasArmed);
    const arm = useAutoAvailableStore((state) => state.arm);
    const sessionStart = useAutoAvailableStore((state) => state.sessionStart);
    const markSessionStart = useAutoAvailableStore((state) => state.markSessionStart);

    const [phase, setPhase] = useState<AutoAvailablePhase>("hidden");
    const [countdownMs, setCountdownMs] = useState(AUTO_AVAILABLE_MS);

    const isOffline = activity === agentSdk.offlineAgentActivity;
    const isAvailable = activity === agentSdk.availableAgentActivity;
    // Not gated on disableAgentStatusChange (that only disables the manual status control); the
    // per-partner rollout flag is the sole control.
    const isFeatureEnabled = isExpertHubAutoAvailableEnabled;

    // Capture how this session started: only a login (started Offline) may arm — a later Offline
    // (logout, or a session begun in another aux status) must not.
    useEffect(() => {
        if (sessionStart !== "unknown" || activity === undefined) return;
        markSessionStart(isOffline ? "offline" : "other");
    }, [sessionStart, activity, isOffline, markSessionStart]);

    const moveToAvailable = useCallback(() => {
        // The countdown can fire during the pill's ~300ms exit transition after the expert already
        // switched status, so re-check now and don't override the choice they just made.
        const isStillOffline = useAgentStore.getState().activity === agentSdk.offlineAgentActivity;
        if (!isStillOffline) return;

        const employeeId = userCache.employeeId;

        const handleMoveSucceeded = () => {
            logger.info({ employeeId }, "Auto-available: moved expert to Available");
            clearAutoAvailableDeadline(); // grace consumed; a later Offline + refresh must not re-fire it
            void getRootDispatcher().dispatchBusinessEvent("ExpertHubAutoAvailableTransitioned", {
                channel: env.channel,
            });
        };

        const handleMoveFailed = (error: unknown) => {
            // Write didn't land; reset to the countdown to retry. warn (not error): a bounded timeout
            // with a graceful fallback is an expected edge case (docs/log-levels-standards.md).
            logger.warn(
                { error, employeeId },
                "Auto-available: could not move expert to Available; resetting to retry",
            );
            void getRootDispatcher().dispatchBusinessEvent("ExpertHubAutoAvailableFailed", { channel: env.channel });
            emitExpertHubError(EXPERT_HUB_ACTIONS.autoAvailableSetFailed, partner);
            setPhase("counting");
        };

        // Optimistically show "Waiting for a call"; the write flips activity to Available, which the
        // phase effect then confirms.
        setPhase("waiting");
        void raceWithTimeout(
            setNewAgentActivity(agentSdk.availableAgentActivity),
            SET_AVAILABLE_TIMEOUT_MS,
            "auto-available",
        ).then(handleMoveSucceeded, handleMoveFailed);
    }, [setNewAgentActivity, agentSdk.availableAgentActivity, agentSdk.offlineAgentActivity, logger, partner]);

    // Arm the countdown once, on a fresh Offline login. resolveCountdownDeadline persists the
    // deadline, so a refresh resumes the same countdown instead of restarting it.
    useEffect(() => {
        if (!isFeatureEnabled || !isOffline || hasArmed || sessionStart !== "offline") return;
        arm();

        const { deadlineMs, createdNewDeadline } = resolveCountdownDeadline(AUTO_AVAILABLE_MS);
        if (createdNewDeadline) {
            void getRootDispatcher().dispatchBusinessEvent("ExpertHubAutoAvailableArmed", { channel: env.channel });
        }

        const remainingMs = deadlineMs - Date.now();
        const gracePeriodElapsed = remainingMs <= 0;
        if (gracePeriodElapsed) {
            moveToAvailable();
            return;
        }
        setCountdownMs(remainingMs);
        setPhase("counting");
    }, [isFeatureEnabled, isOffline, hasArmed, sessionStart, arm, moveToAvailable]);

    // Keep the pill phase in sync with the agent's status.
    useEffect(() => {
        if (!isFeatureEnabled) {
            setPhase("hidden");
            return;
        }
        // While counting, the arm effect and moveToAvailable own the phase, so preserve it. Any other
        // Offline (after being Available, or logout) clears a stale "waiting" pill.
        if (isOffline) {
            setPhase((current) => (current === "counting" ? current : "hidden"));
            return;
        }
        setPhase(isAvailable ? "waiting" : "hidden");
    }, [isFeatureEnabled, isOffline, isAvailable]);

    return { active: phase !== "hidden", phase, countdownMs, onExpire: moveToAvailable };
}
